/**
 * LANE V2 → THE WEAVE CONVERSATION. Pure rules that turn `LaneV2State` into what `WeaveTranscript`'s
 * view layer renders (`TurnView` takes a protocol `Turn`), plus the composer/ask rules the session
 * view used to own.
 *
 * WHY A MAPPER AND NOT A SECOND TRANSCRIPT. Everything below `sessionTurns()` in the v1 transcript is
 * pure over `Turn { user: Message | null; assistant: Message[] }`, and the only `Part` fields it reads
 * are `type`, `state`, `index` and `body.text`. R5 gives a turn's `user_text` and `markdown`; that is
 * exactly a user text part and an assistant text part, so the same view renders both lanes. The
 * synthesised ids are marked (`msg_v2_…`), the same way `withUserTurn` marks its synthetic user
 * message on v1 — nothing here claims to be a protocol message that crossed the wire.
 *
 * NOTHING HERE IS A STATE MACHINE. The task's state comes from R3 and is rendered as-is by the task
 * views; a turn is "live" exactly when the server says so (`activeTurnId`, `ACTIVE_TURN_STATUSES`) or
 * when the client's own send has not been acknowledged yet (`LocalTurn.status`).
 */
import type { Message, Part, PartState } from "../protocol";
import { turnPhase, type Turn, type TurnPhase } from "../transcript";
import type { StreamStatusViewProps } from "@/components/molecules/multi-agent/StreamStatus";
import { ACTIVE_TURN_STATUSES, echoAttachments, orderedTasks, pendingAsks, timeline, type ConversationAttachment, type LaneV2State, type LocalTurn, type TimelineEntry } from "./reduce";
import type { AskView, TaskSnapshot, TaskSummary, UsageTotals } from "./types";

// ── the rules the session view used to own (unchanged, moved) ───────────────────────────────────

/** Subjects whose ask must be shown next to the task snapshot it is about. */
const CONTEXT_SUBJECTS: ReadonlySet<string> = new Set(["plan_revision", "package", "apply", "revert"]);

export function askNeedsTaskContext(ask: AskView): boolean {
  const type = ask.interaction.subject?.type;
  return !!ask.task_id && !!type && CONTEXT_SUBJECTS.has(type);
}

/**
 * An `approve_apply` ask on a FORWARD task (one with a build) is a retired card (contracts 0.2.30, no-deploy program): the
 * server no longer raises it, answering one is refused with 409 `DEPLOY_NOT_OFFERED`, and any left pending on an older session is
 * withdrawn server-side. Until that sweep lands it is not rendered or counted as pending here. What follows a build is the
 * server-driven Save / Validate actions (`TaskSnapshot.actions`), not an ask.
 *
 * A REVERT task keeps its `approve_apply` (it has no build, and reverting is untouched), so the test is the task's build, not the
 * kind alone. A task whose snapshot is not loaded yet cannot be told apart and keeps the card.
 */
export function isForwardDeployAsk(ask: AskView, snapshot: Pick<TaskSnapshot, "build"> | null | undefined): boolean {
  // An ANSWERED approve_apply is history (an older session where the reader did approve an apply) and stays in the transcript.
  return ask.interaction.kind === "approve_apply" && ask.status !== "answered" && !!snapshot && snapshot.build != null;
}

export function withoutForwardDeployAsks(
  asks: readonly AskView[],
  snapshots: Readonly<Record<string, Pick<TaskSnapshot, "build"> | undefined>>,
): readonly AskView[] {
  const retired = (a: AskView) => isForwardDeployAsk(a, a.task_id ? snapshots[a.task_id] : null);
  return asks.some(retired) ? asks.filter((a) => !retired(a)) : asks;
}

/** Is a turn running (server-side active turn, or a send still in flight)? */
export function turnInFlight(state: Pick<LaneV2State, "activeTurnId" | "localTurns">): boolean {
  return !!state.activeTurnId || state.localTurns.some((t) => t.status === "sending");
}

/**
 * A plan is up for approval: its card carries its own choices AND its own text box (`PlanQuestionsCard`), so the composer is not
 * where it is answered and must not say "Pick an option above" (dev 2026-11-02: two places to type). Read off the ask's typed
 * subject, so a plan revision and an amendment (both `plan_revision` subjects) count, and no other kind of question does.
 */
export function planDecisionPending(asks: readonly AskView[]): boolean {
  return pendingAsks(asks).some((a) => a.interaction.subject?.type === "plan_revision");
}

export function composerPlaceholder(state: Pick<LaneV2State, "asks" | "draft" | "tasks"> & { turns?: readonly unknown[] }): string {
  if (state.draft || (state.tasks.length === 0 && !(state.turns?.length))) return "Ask about your org, or describe a change…";
  return "Ask a follow-up…";
}

/** Why a task could not be cancelled, in words. */
export function cancelRefusalCopy(reason: string | null): string {
  if (reason === "apply_in_progress") return "This task is applying changes to your org right now and can't be cancelled until that finishes.";
  if (reason === "save_in_progress") return "A save to the branch is still running, so nothing was cancelled. Try again after the save finishes.";
  if (reason === "task_not_cancellable") return "This task can no longer be cancelled.";
  return "This task could not be cancelled.";
}

/**
 * Whether a chosen answer should be refused CLIENT-SIDE, before it ever reaches the server, given the
 * R0 `offline` state. Mirrors the server's own rule exactly, not an approximation of it: the only
 * option_id the server ever gates offline is `"approve"` (`core/asks.py:73`, `core/amendments.py:64`,
 * `lane_v2/routes.py:312-314`) — a park ask's `"ack"`, a decline/request-changes, and a free-text or
 * batch-clarify answer (`option_id` `null`) are all allowed offline.
 */
export function offlineRefusesAnswer(offline: boolean, optionId: string | null | undefined): boolean {
  return offline && optionId === "approve";
}

// ── the transcript entries ───────────────────────────────────────────────────────────────────────

export interface ConversationTurnEntry {
  kind: "turn";
  key: string;
  turn: Turn;
  /**
   * The server's turn id, or null for an echo the server has not acknowledged. A v2 turn IS a
   * `multi_agent_runs` row (MSA `dialogue/turns.py`), which is what run-keyed side channels
   * (feedback) take.
   */
  turnId: string | null;
  /** R5 `task_id` — the task this turn opened or acted on, so its card can sit right after it. */
  taskId: string | null;
  /** R5 `outcome` on a settled server turn (MSA `DispatchOutcome`), or null. */
  outcome: string | null;
  /** Still being produced: the server's active turn, or a send the server has not acknowledged. */
  live: boolean;
  /** `turnPhase`'s vocabulary, so the transcript renders the same skeleton/cursor/closing it does on v1. */
  phase: TurnPhase;
  /** R5 `error_code` on a settled server turn, or null. */
  errorCode: string | null;
  /** A local send that failed — the reason, or null. The echo stays so the reader can retry or remove. */
  failure: string | null;
  usage: UsageTotals | null;
  /** Shown under the reader's own question (never after the answer). */
  attachments: ConversationAttachment[];
  /** R5 `trace.tool_calls` of a settled server turn (leg `gui-what-i-read`); null/absent when unknown. */
  toolCalls?: number | null;
}

export interface ConversationAskEntry {
  kind: "ask";
  key: string;
  ask: AskView;
}

export type ConversationEntry = ConversationTurnEntry | ConversationAskEntry;

const V2_MESSAGE_PREFIX = "msg_v2_";

function textPart(messageId: string, text: string, state: PartState): Part {
  return { part_id: `${messageId}_0`, message_id: messageId, index: 0, type: "text", state, body: { text } };
}

function textMessage(role: "user" | "assistant", turnId: string, text: string, streaming: boolean): Message {
  const messageId = `${V2_MESSAGE_PREFIX}${role}_${turnId}`;
  return {
    message_id: messageId,
    role,
    turn_id: turnId,
    status: streaming ? "streaming" : "complete",
    parts: [textPart(messageId, text, streaming ? "streaming" : "available")],
  };
}

/** A protocol `Turn` from what R5 (or the local echo) knows: the question and the markdown answer. */
export function conversationTurn(turnId: string, userText: string | null, markdown: string | null, live: boolean): Turn {
  const user = userText && userText.trim() ? textMessage("user", turnId, userText, false) : null;
  const assistant = markdown && markdown.trim() ? [textMessage("assistant", turnId, markdown, live)] : [];
  return { user, assistant };
}

function serverEntry(entry: Extract<TimelineEntry, { kind: "turn" }>, activeTurnId: string | null): ConversationTurnEntry {
  const live = entry.turnId === activeTurnId || ACTIVE_TURN_STATUSES.has(entry.status);
  const turn = conversationTurn(entry.turnId, entry.userText, entry.markdown, live);
  return {
    kind: "turn",
    key: entry.key,
    turn,
    turnId: entry.turnId,
    taskId: entry.taskId,
    outcome: live ? null : entry.outcome,
    live,
    // A settled turn is `complete` whatever it holds; a live one narrates from its parts, the same
    // way `turnPhase` does for a v1 run whose status is not terminal yet.
    phase: live ? turnPhase(null, turn) : "complete",
    errorCode: live ? null : entry.errorCode,
    failure: null,
    usage: live ? null : entry.usage,
    attachments: entry.attachments,
    toolCalls: live ? null : entry.toolCalls ?? null,
  };
}

function localEntry(key: string, local: LocalTurn): ConversationTurnEntry {
  const live = local.status !== "failed";
  const turn = conversationTurn(local.turnId ?? `local:${local.localId}`, local.text, local.markdown ?? null, live);
  return {
    kind: "turn",
    key,
    turn,
    turnId: local.turnId,
    taskId: null,
    outcome: null,
    live,
    phase: live ? turnPhase(null, turn) : "complete",
    errorCode: null,
    failure: local.status === "failed" ? local.error ?? "Your message was not sent." : null,
    usage: null,
    attachments: echoAttachments(local),
  };
}

/**
 * The conversation, in the order `timeline()` already decides (server turns and asks by time, then
 * the client's unacknowledged echoes), each turn shaped for `TurnView`.
 */
export function conversationEntries(
  state: Pick<LaneV2State, "turns" | "asks" | "localTurns" | "activeTurnId">,
): ConversationEntry[] {
  return timeline(state).map<ConversationEntry>((entry) => {
    if (entry.kind === "turn") return serverEntry(entry, state.activeTurnId);
    if (entry.kind === "local") return localEntry(entry.key, entry.local);
    return { kind: "ask", key: entry.key, ask: entry.ask };
  });
}

/** The newest turn — what the completion block, the follow-ups and the export are about. */
export function lastTurnEntry(entries: readonly ConversationEntry[]): ConversationTurnEntry | null {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry.kind === "turn") return entry;
  }
  return null;
}

/**
 * The newest turn raised a connected-tool write card (Jira, GitHub, Notion…), so its follow-ups are not Salesforce ones
 * (MSA QA 86bcm3a4p). A card follows its turn in time order: only asks after the newest turn count.
 */
export function lastTurnRaisedConnectorWrite(entries: readonly ConversationEntry[]): boolean {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry.kind === "turn") return false;
    if (entry.kind === "ask" && (entry.ask.interaction.kind as string) === "approve_connector_write") return true;
  }
  return false;
}

/** The answer's own markdown, for Copy / Export / the follow-up catalogue. */
export function answerMarkdownOf(entry: ConversationTurnEntry | null): string {
  if (!entry) return "";
  return entry.turn.assistant
    .flatMap((message) => message.parts ?? [])
    .filter((part) => part.type === "text")
    .map((part) => ((part.body ?? {}) as { text?: unknown }).text)
    .filter((text): text is string => typeof text === "string")
    .join("\n\n")
    .trim();
}

// ── how a turn ended, in words ───────────────────────────────────────────────────────────────────

/** Words for MSA `UnavailableReason` (`core/errors.py`), the closed reason a lane could not run. Nothing was changed in any of them. */
const TURN_FAILURE_BY_REASON: Record<string, string> = {
  model_error: "The AI model had a problem answering. Nothing was changed. Try again.",
  invalid_output: "The AI model's answer came back malformed. Nothing was changed. Try again.",
  deadline: "This took too long and was stopped. Nothing was changed. Try again.",
  budget_exhausted: "Your usage budget is used up, so this couldn't run. Nothing was changed.",
  budget_blocked: "Your usage budget is used up, so this couldn't run. Nothing was changed.",
};

const TURN_FAILURE_FALLBACK = "Something went wrong and this didn't finish. Nothing was changed. Try again.";

/**
 * A failed turn in a reader's words, from the typed `reason` when the caller has it (the live `TurnOut.detail.reason`), else from
 * the `error_code` when that code names one, else a plain fallback. The code itself is never the sentence: callers show it
 * muted as "(ref CODE)" beside these words (UX map C13).
 */
export function turnFailureWords(_errorCode: string | null | undefined, reason?: string | null): string {
  // The code alone never names a reason (`LANE_UNAVAILABLE` covers every one); only a typed `reason` does.
  return (reason && TURN_FAILURE_BY_REASON[reason]) || TURN_FAILURE_FALLBACK;
}

/** The muted reference suffix for a failure code. */
export function refSuffix(errorCode: string): string {
  return `(ref ${errorCode})`;
}

/**
 * The closing sentence for a settled v2 turn, from R5 `outcome` (MSA `core/dispatch.py`
 * `DispatchOutcome`) and `error_code`. The same role `closingLine` plays for a v1 run.
 *
 * `null` for outcomes the transcript already shows in another way: `answered` (the answer is the
 * line), `ask_raised` / `awaiting_reply` / `preselected` (the ask card follows), `control_status` /
 * `control_continue` (the markdown IS the status). Unknown outcomes are also `null`: the vocabulary
 * is the server's and a sentence invented for a value this client does not know would be a claim.
 *
 * `serverText`: the turn response carried its own words (`TurnOut.markdown`, in the transcript as the
 * assistant message). The server's words own the closing, so the client prints none — `null` — except a failed
 * turn's error code, as a suffix line.
 */
export function turnClosingLine(
  outcome: string | null | undefined,
  errorCode: string | null | undefined,
  serverText: boolean = false,
): { text: string; tone: "ok" | "warn" | "bad"; ref?: string } | null {
  if (serverText) {
    // The server's words own the closing, a failure included: print no second sentence, only the reference (if any) as a
    // small muted suffix line. Without server text (older server, or a reload: R5 carries none) the full client line stays.
    if (outcome === "failed" && errorCode) return { text: "", tone: "bad", ref: errorCode };
    return null;
  }
  switch (outcome) {
    case "plan_proposed":
      return { text: "Proposed a plan for your approval.", tone: "ok" };
    case "task_in_progress":
      return { text: "A task is already in progress in this conversation. Ask again once it finishes, or cancel it.", tone: "warn" };
    case "no_open_task":
      return { text: "There is no open task to act on.", tone: "warn" };
    case "blocked_legacy_ask":
      return { text: "A question from the old chat experience is still open. Start a new conversation.", tone: "warn" };
    case "control_refused":
      return { text: "That could not be done from the chat.", tone: "warn" };
    case "control_cancelled":
      return { text: "Cancelled the task.", tone: "ok" };
    case "cancelled":
      return { text: "Stopped before finishing.", tone: "warn" };
    case "failed":
      return errorCode ? { text: turnFailureWords(errorCode), tone: "bad", ref: errorCode } : { text: turnFailureWords(null), tone: "bad" };
    default:
      return null;
  }
}

// ── tasks: beside an ask, or listed ─────────────────────────────────────────────────────────────

/** Task ids rendered next to a pending ask (so they are not repeated in the list). */
export function tasksBesideAsks(state: Pick<LaneV2State, "asks" | "snapshots">): Set<string> {
  return new Set(
    pendingAsks(state.asks)
      .filter(askNeedsTaskContext)
      .map((a) => a.task_id as string)
      .filter((id) => !!state.snapshots[id]),
  );
}

/** Tasks for the reference cards: every task with a snapshot that is not already beside an ask. */
export function listedTasks(state: Pick<LaneV2State, "asks" | "snapshots" | "tasks">): TaskSummary[] {
  const beside = tasksBesideAsks(state);
  return orderedTasks(state.tasks).filter((t) => !beside.has(t.task_id) && !!state.snapshots[t.task_id]);
}

// ── stream health, in the transcript's own vocabulary ───────────────────────────────────────────

/**
 * `StreamStatusView`'s facts from the session stream. Never desynced (the read model has no
 * sequence gap to detect — a reconnect re-reads R1/R2/R5/R3 wholesale), never terminal (a session
 * has no end the way a run does), and before the first attach there is nothing to be stale about.
 */
export function streamStatusViewFor(
  state: Pick<LaneV2State, "streamStatus" | "draft">,
): Pick<StreamStatusViewProps, "connection" | "desynced" | "streamError" | "terminal" | "hasState" | "reconnectAttempt"> {
  return {
    connection: state.streamStatus,
    desynced: false,
    streamError: null,
    terminal: false,
    hasState: !state.draft,
    reconnectAttempt: null,
  };
}

/** What the lost-connection banner says on a session: no run to report on, only the session. */
export const SESSION_STREAM_LOST_DETAIL =
  "The connection could not be re-established. Anything still running continues on the server; what you see below is the last state received.";
